/* eslint-disable no-nested-ternary */
/* eslint-disable consistent-return */
/* eslint-disable react/no-array-index-key */
import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import Dropdown from 'rc-dropdown';
import Menu, { Item as MenuItem, SubMenu } from 'rc-menu';
import styled from '@emotion/styled';
import { Global, css } from '@emotion/core';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { useTheme } from 'emotion-theming';

import Text from '../../../../shared/components/Text/Text';
import SuperSetPickerPopup from './SuperSetPickerPopup';
import PacePickerPopup from './PacePickerPopup';
import 'rc-dropdown/assets/index.css';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import {
  setActiveModal,
  revertSwappedItemToParent,
  updateCalendarItemsOrder,
  updateWorkoutItemsOrder,
  setCombineWithInfo,
  setCalendarItems,
  setNonDateBasedProgramItems,
  setRecurringCalendarItems,
  setIsCalendarOptionsDropdownMenuOpen,
  setSwapDropdownToggleEnabled,
  setIsPrimaryAnimatedSideBarOpen,
  setPrimarySideBarDataTag,
} from '../../ducks/calendarActions';
import { setFilterOptions as setJournalFilterOptions } from '../../../journal/ducks/journalActions';
import { cancelDebounceForTable, flushDebounceForTable } from './hooks/debounceHandler';
import { isPlayableVideoUrl } from './hooks/useValidateUrl';
import { claimArrowKeys, hasArrowKeyOwner } from '../../utils/arrowKeyOwnership';
import getExerciseInfoCategory from '../../../../shared/utils/getExerciseInfoCategory';
import themedScrollbar from './themedScrollbar';
import getModifierKeyLabel from '../../../../shared/utils/getModifierKeyLabel';
import { EXERCISE_OPTION_IDS } from '../../constants/exerciseOptions';

const OPTIONS_SHORTCUT_LABEL = `${getModifierKeyLabel()} + O`;

/** Turns one shared option descriptor into this menu's row shape. `legacyKey`
 * keeps the long-standing string keys, which are React keys and submenu
 * comparands elsewhere in the file. */
const buildOptionMenuItem = (option, legacyKey) => {
  /** No descriptor means no `exerciseOptions` was passed — render nothing
   * rather than throwing, since the prop is optional. */
  if (!option) {
    return {
      key: legacyKey, label: '', action: () => {}, inActive: true,
    };
  }
  return {
    key: legacyKey,
    label: option.menuLabel,
    icon: option.icon,
    action: option.toggle,
    isChecked: option.isChecked,
    inActive: !option.isAvailable,
    /** Deliberately not `!option.canToggle`: that would also grey isLocked
     * rows, and those are specified to render normally here and silently
     * no-op. Only greyed-by-item, blocked-by-sibling and item-soft-locked
     * (save or swap in flight) rows look disabled. */
    disabled: option.isGreyedOut || option.isBlocked || option.isItemLocked,
    ...(option.hasDisabledSlash && { extra: <RedDashThrough /> }),
  };
};

/** `shortcut` is overloaded: 'superset' and 'pace' are sentinels meaning
 * "render a submenu arrow and nest a picker"; any other value is the
 * literal shortcut text shown on the right of the row. */
const isSubmenuItem = (item) => item?.shortcut === 'superset' || item?.shortcut === 'pace';

const DropdownIcon = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;

`;

const DropdownContainer = styled('div')`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  justify-content: center;
  padding: 7px;
  border-radius: 4px;
  cursor: pointer;
  :hover {
   background: rgba(119, 167, 255, 0.2);
  }

  .AdditionalOptionsDots {
    fill: ${({ theme }) => theme.colors.textSecondary};
  }

  &:hover .AdditionalOptionsDots  {
    fill: ${({ theme }) => theme.colors.hover};
  }
`;

const MenuWrapper = styled('div')`
  .custom-rc-menu {
    border-radius: 4px !important;
    box-shadow: 0px 4px 8px 0px #3031331A !important;
    border: 1px solid ${({ theme }) => theme.colors.borderLight} !important;

    padding-top: 8px;
    padding-bottom: 8px;
    
    ${themedScrollbar}
  }

  .rc-menu-item-active,
  .rc-menu-item-selected,
  .rc-menu-submenu-active > .rc-menu-submenu-title {
    background-color: transparent !important;
  }

  .rc-menu-submenu-title {
    padding: 0px !important;
  }

  .rc-menu-submenu-arrow {
    display: none;
  }

`;

const MenuHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
`;

const CustomDivider = styled.div`
  height: 1px;
  margin: 8px 0;
  background-color: ${({ theme }) => theme.colors.borderLight};

  margin-left: 18px;
  margin-right: 18px;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
`;

const RedDashThrough = styled.div`
  width: 24px;
  height: 2px;
  background: ${({ theme }) => theme.colors.textTertiary};
  position: absolute;
  transform: rotate(45deg);
  transform-origin: center;
  left: -3px;
  top: 10px;
`;

const ParentIconWrapper = styled('div')`
  height: 16px;
  width: 16px;
  background: #B5B5B5;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ParentIconBorderBox = styled('div')`
  border: 1px solid ${({ theme }) => theme.colors.white};
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 12px;
  width: 12px;
  text-align: center;
`;

const VisibleClickDiv = styled('div')`

`;

// eslint-disable-next-line react/prop-types
const HoverableMenuItem = ({
  hoverableItem,
  content,
  isRecurring,
  assignedPace,
  setAssignedPace,
  effectiveDate,
  dayNumber,
  measure,
  isFocused,
  isKeyboardOpen,
  onExitSubmenu,
  navIndex,
}) => {
  const [openKeys, setOpenKeys] = useState([]);
  const [subMenuVisible, setSubMenuVisible] = useState(false);
  const calendarContainerScrollPosition = useSelector(
    (state) => state.calendar.data.calendarContainerScrollPosition,
  );

  useEffect(() => {
    /** close select menu if main container is scrolled */
    if (subMenuVisible) {
      setSubMenuVisible(false);
    }
  }, [calendarContainerScrollPosition]);

  useEffect(() => {
    const handleResize = () => {
      if (subMenuVisible) {
        setSubMenuVisible(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [subMenuVisible]);

  const handleOpenChange = (keys) => {
    // Only allow one submenu to be open at a time
    if (keys.length > 1) {
      setOpenKeys([keys[keys.length - 1]]); // Keep only the most recently opened
    } else {
      setOpenKeys(keys);
    }
  };

  const [isHovered, setIsHovered] = useState(false);
  const { colors } = useTheme();

  /** Mouse hover and keyboard focus share one highlight — the coach only
   * ever has one of them pointed at a row. */
  const isHighlighted = isHovered || isFocused;

  /** One field now drives colors, cursor, onClick and the nav filter. It used
   * to be split: a module-level helper matched Completion Only by its label
   * string to grey the text, while `hoverableItem.disabled` - which nothing
   * ever set - gated the cursor and the click. So a greyed row still fired its
   * action and still took the hover highlight, and was inert only because the
   * descriptor's own toggle early-returned. */
  const disabled = !!hoverableItem.disabled;
  const menuItemContent = (
    <div
      style={{
        display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', position: 'relative',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: hoverableItem.shortcut === 'superset' ? '0px' : '8px', cursor: disabled && 'not-allowed', minWidth: 0,
      }}
      >
        <IcomoonReact
          iconSet={iconSet}
          size={hoverableItem.shortcut === 'superset' ? 24 : 16}
          icon={hoverableItem.icon}
          color={disabled ? colors.textTertiary : (hoverableItem.color || colors.textSecondary)}
        />
        {hoverableItem.extra}
        <Text
          fontSize='16px'
          color={disabled ? colors.textTertiary : colors.text}
          style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}
        >
          {hoverableItem.label}
        </Text>
      </div>
      {hoverableItem.isChecked !== undefined && hoverableItem.isChecked && (
        <IcomoonReact iconSet={iconSet} size={12} icon='checkmark' color={disabled ? colors.textTertiary : colors.text} />
      )}
      {hoverableItem.shortcut && (

        hoverableItem.shortcut === 'superset' || hoverableItem.shortcut === 'pace'
          ? <IcomoonReact iconSet={iconSet} size={10} icon='altrightarrow' color={colors.text} />
          : <Text color={colors.text}>{hoverableItem.shortcut}</Text>

      )}
    </div>
  );

  if (hoverableItem.shortcut === 'superset' || hoverableItem.shortcut === 'pace') {
    return (
      <Menu
        triggerSubMenuAction='click'
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        style={{
          border: 'none',
          background: 'transparent',
          boxShadow: 'none',
          padding: 0,
          margin: 0,
        }}
        className='nested-submenu'
      >
        <VisibleClickDiv onClick={() => setSubMenuVisible(true)}>
          <SubMenu
            key={hoverableItem.key}
            title={(
              <div
                data-nav-index={navIndex}
                style={{
                  paddingTop: 7,
                  paddingBottom: 7,
                  paddingLeft: 16,
                  paddingRight: 16,
                  cursor: hoverableItem.disabled ? 'not-allowed' : 'pointer',
                  background: isHighlighted ? colors.surfaceSecondary : colors.surfaceElevated,
                }}
              >
                {menuItemContent}
              </div>
        )}
            onTitleMouseEnter={() => setIsHovered(true)}
            onTitleMouseLeave={() => setIsHovered(false)}
            popupClassName={
            /** Visible when either the React subMenuVisible flag OR the
             * rc-menu openKeys is set — whichever resolves first in the
             * same event tick. Prevents a one-frame display:none flash
             * that was hiding the picker in the month-view popover. */
            `${hoverableItem.shortcut === 'superset' ? 'custom-submenu-supersetPicker' : 'pace-dropdown'}${!subMenuVisible && openKeys.length === 0 ? ' custom-submenu-supersetPicker-displayNone' : ''}`
            }
          >
            <MenuItem key={`${hoverableItem.key}-popup`} style={{ padding: 0 }}>
              {hoverableItem.shortcut === 'superset' ? (
                <SuperSetPickerPopup
                  content={content}
                  left={0}
                  top={0}
                  spaceAbove={null}
                  isRecurring={isRecurring}
                  effectiveDate={effectiveDate}
                  dayNumber={dayNumber}
                  isKeyboardNavActive={isKeyboardOpen}
                  onExitSubmenu={onExitSubmenu}
                />
              ) : (
                <PacePickerPopup
                  left={0}
                  top={0}
                  spaceAbove={null}
                  assignedPace={assignedPace}
                  setAssignedPace={setAssignedPace}
                  measure={measure}
                  isKeyboardNavActive={isKeyboardOpen}
                  onExitSubmenu={onExitSubmenu}
                />
              )}
            </MenuItem>
          </SubMenu>
        </VisibleClickDiv>
      </Menu>
    );
  }

  return (
    <MenuItem
      key={hoverableItem.key}
      style={{
        paddingTop: 7,
        paddingBottom: 7,
        paddingLeft: 16,
        paddingRight: 16,
        cursor: disabled ? 'not-allowed' : 'pointer',
        /** No highlight on a row that can't be actioned, matching the Options
         * dropdown. */
        background: isHighlighted && !disabled ? colors.surfaceSecondary : 'transparent',
      }}
      onClick={disabled ? null : (e) => hoverableItem.action(e)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <IconContainer data-nav-index={navIndex}>
        {menuItemContent}
      </IconContainer>
    </MenuItem>
  );
};

HoverableMenuItem.propTypes = {
  hoverableItem: PropTypes.shape({
    key: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string,
    disabled: PropTypes.bool,
    action: PropTypes.func,
    extra: PropTypes.node,
    isChecked: PropTypes.bool,
    shortcut: PropTypes.string,
  }).isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  isRecurring: PropTypes.string,
  assignedPace: PropTypes.string,
  setAssignedPace: PropTypes.func,
  effectiveDate: PropTypes.string.isRequired,
  dayNumber: PropTypes.number.isRequired,
  measure: PropTypes.func.isRequired,
  isFocused: PropTypes.bool,
  isKeyboardOpen: PropTypes.bool,
  onExitSubmenu: PropTypes.func,
  navIndex: PropTypes.number,
};

HoverableMenuItem.defaultProps = {
  isRecurring: '',
  assignedPace: '',
  setAssignedPace: () => {},
  isFocused: false,
  isKeyboardOpen: false,
  onExitSubmenu: () => {},
  navIndex: -1,
};

const CalendarItemDropdownOptions = ({
  content,
  index,
  isExpanded,
  setIsExpanded,
  expandId,
  iconOb,
  setIconOb,
  contentType,
  handleRemovePresaveItemFromRedux,
  localAdditionalInfo,
  localDescription,
  isRecurring = '',
  assignedPace,
  setAssignedPace,
  // eslint-disable-next-line no-unused-vars
  isWeekView = false,
  effectiveDate = null,
  dayNumber = null,
  measure,
  flags,
  // eslint-disable-next-line react/prop-types
  hideExpandCollapse,
  isDragging = false,
  exerciseOptions = [],
}) => {
  /** The shared option toggles, keyed for random access. Built by
   * useExerciseOptionToggles in DraggableContent so this menu and the tables'
   * Options dropdown run the exact same handlers. */
  const optionsById = Object.fromEntries(
    exerciseOptions.map((option) => [option.id, option]),
  );
  const [localDropdownVisible, setLocalDropdownVisible] = useState(false);
  const dispatch = useDispatch();
  const { colors } = useTheme();
  const [maxDropdownHeight, setMaxDropdownHeight] = useState(500);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  /** Index into `navItems` (dividers and unavailable rows excluded), or -1
   * while the menu is closed. Highlight only — DOM focus stays wherever the
   * coach left it, matching the Add Property dropdown. */
  const [focusedIndex, setFocusedIndex] = useState(-1);
  /** The row whose nested picker currently owns the keyboard, as
   * `{ key, navIndex }` — the index is kept so the same row can be clicked
   * again to toggle the submenu shut on the way out. */
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const activeExerciseId = useSelector((state) => state.calendar.data.activeExerciseId);
  const allExercises = useSelector((state) => state.calendar.data.allExercises);
  const programType = useSelector((state) => state.calendar.data.programType);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountCode = currentUser?.accountCode;
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const calendarContainerScrollPosition = useSelector(
    (state) => state.calendar.data.calendarContainerScrollPosition,
  );
  /** A modal opened from this menu owns Escape — see the capture-phase
   * listener below. Selected as a boolean rather than the two raw values so
   * this per-row subscription only re-renders when the flag actually flips,
   * not on every modalData change. */
  const isAnyModalOpen = useSelector((state) => !!(
    state.calendar.data.activeModal || state.calendar.data.activeSecondLayerModal
  ));
  const date = new Date(effectiveDate || selectedDate);
  const endpointFormattedDate = moment(date).format('YYYY-MM-DD');
  const calendarItemsForSelectedDate = programType === 'free-form-program'
    ? useSelector((state) => state.calendar.data.nonDateBasedProgramItems[
      dayNumber || selectedProgramDayNumber
    ]?.exercises || []) : useSelector(
      (state) => state.calendar.data.calendarItems[endpointFormattedDate]?.exercises || [],
    );
  const beforeRecurringWorkouts = useSelector(
    (state) => state.calendar.data.beforeRecurringWorkouts[endpointFormattedDate] || [],
  );
  const afterRecurringWorkouts = useSelector(
    (state) => state.calendar.data.afterRecurringWorkouts[endpointFormattedDate] || [],
  );

  const selectedReduxAthlete = useSelector(
    (state) => state.calendar.data.selectedReduxAthlete,
  );
  const journalMediaOnly = useSelector((state) => state.journal.data.mediaOnly);
  const journalDateRange = useSelector((state) => state.journal.data.dateRange);
  const hasSelectedAthleteInBuild = !!Object.keys(selectedReduxAthlete || {}).length;
  const journalExercise = content?.preSave ? content : content?.exercise;

  const handleViewInJournal = () => {
    dispatch(setIsPrimaryAnimatedSideBarOpen(true));
    dispatch(setPrimarySideBarDataTag('journal'));
    dispatch(setJournalFilterOptions(
      journalMediaOnly,
      journalDateRange?.startDate,
      journalDateRange?.endDate,
      { id: journalExercise?.id, name: journalExercise?.name },
    ));
    setLocalDropdownVisible(false);
  };

  const hasMounted = useRef(false);
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    if (localDropdownVisible) {
      dispatch(setIsCalendarOptionsDropdownMenuOpen(true));
    } else {
      dispatch(setIsCalendarOptionsDropdownMenuOpen(false));
    }
    const handleResize = () => {
      if (localDropdownVisible) {
        setLocalDropdownVisible(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [localDropdownVisible]);

  useEffect(() => {
    if (localDropdownVisible) {
      setLocalDropdownVisible(false);
    }
  }, [calendarContainerScrollPosition]);

  /** Own the arrow keys while the menu is open, so useCalendarKeyboardScroll
   * doesn't scroll the calendar underneath — that scroll dispatches
   * calendarContainerScrollPosition, and the effect above would read it as the
   * coach scrolling and close the menu mid-navigation. Covers the Pace and
   * Superset pickers too: the menu stays open behind them. */
  useEffect(() => {
    if (!localDropdownVisible) return undefined;
    return claimArrowKeys();
  }, [localDropdownVisible]);

  /** Shared by the click and ⌘+O paths. The height has to be measured at
   * open time from the trigger's position, so the keyboard path can't just
   * flip `localDropdownVisible` — it would leave the stale default height. */
  const openDropdown = useCallback(() => {
    const dropdownIcon = dropdownRef.current?.getBoundingClientRect();
    if (dropdownIcon) {
      const windowHeight = window.innerHeight;
      const calculatedMaxHeight = windowHeight - dropdownIcon.top - 25;
      const maxHeight = dropdownIcon.top - 100 > calculatedMaxHeight
        ? dropdownIcon.top - 25
        : calculatedMaxHeight;
      setMaxDropdownHeight(maxHeight);
    }
    setLocalDropdownVisible(true);
  }, []);

  /** ⌘/Ctrl+O opens this exercise's menu. preventDefault runs before the
   * active-exercise gate so the browser's native "Open File" dialog never
   * appears while a calendar row is mounted — Calendar.js's hub can't do
   * this, as it bails early whenever focus is in an input, which is exactly
   * where focus is when the coach is filling in a table cell. */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'o') return;
      e.preventDefault();
      if (isDragging) return;
      /** A picker is already navigating with the keyboard. Opening this menu
       * on top of it would leave both arrowing off one keypress, since this
       * menu is highlight-only and can't take focus off the picker. */
      if (hasArrowKeyOwner()) return;
      if (content.assignedId !== activeExerciseId) return;
      openDropdown();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeExerciseId, content.assignedId, isDragging, openDropdown]);

  const shouldAddInfoShow = (paramContent) => getExerciseInfoCategory(paramContent) !== 'none';

  const isNonDuplicableType = ['session_break', 'rest', 'video', 'P'].includes(content.type)
        || content?.exercise?.type === 'P';

  /** Video items open the player instead of the item-info sheet — but only when the
   * player can actually play the URL, mirroring the green check on the URL input.
   */
  const isPlayableVideoItem = content?.type === 'video' && isPlayableVideoUrl(content?.url);

  const infoCategory = getExerciseInfoCategory(content);
  const hasAdditionalInfo = !!(localAdditionalInfo
    || content?.additionalInfo || content?.exercise?.additionalInfo);
  const hasDescription = !!(localDescription || content?.description);
  const getInfoMenuLabel = () => {
    if (infoCategory === 'both') {
      return `${(hasAdditionalInfo || hasDescription) ? 'Edit' : 'Add'} Description Or Additional Information`;
    }
    if (infoCategory === 'description') {
      return `${hasDescription ? 'Edit' : 'Add'} Description`;
    }
    return `${hasAdditionalInfo ? 'Edit' : 'Add'} Additional Information`;
  };

  const menuItems = [
    {
      key: '1',
      label: isPlayableVideoItem ? 'View Video' : 'View Item Info',
      icon: isPlayableVideoItem ? 'video-camera' : 'preview',
      action: () => {
        /** A video item has no content.exercise, so branch before the lookup below. */
        if (isPlayableVideoItem) {
          dispatch(setActiveModal('videoModal', { url: content.url }));
          setLocalDropdownVisible(false);
          return;
        }
        const contentExercise = allExercises.find(
          (exercise) => exercise?.id === content?.exercise?.id,
        );
        dispatch(setActiveModal('createNewComponentModal', {
          type: content?.preSave ? content?.type : content?.exercise.type,
          editModal: true,
          disabledEditModal: true,
          itemInfoForEditModal: content?.preSave ? content
            : (!content?.preSave && !content.reusableItem)
              ? content?.exercise : contentExercise,
          isPreviewOnly: true,
        }));
        setLocalDropdownVisible(false);
      },
      inActive: contentType === 'session_break' || content?.type === 'rest' || contentType === 'H' || (content?.type === 'video' && !isPlayableVideoItem) || content?.type === 'progression' || content.type === 'P' || content.type === 'T' || content.type === 'tag',
    },
    {
      key: '1a',
      label: isExpanded ? 'Collapse' : 'Expand',
      icon: 'collapse',
      action: () => setIsExpanded(expandId),
      inActive: hideExpandCollapse,
    },
    {
      key: '2',
      label: 'Copy',
      icon: 'duplicate',
      action: () => {},
      inActive: true,
    },
    {
      key: '3',
      label: 'Swap',
      icon: 'swap-alt',
      action: () => {
        const newIconOb = { ...iconOb };
        newIconOb.swapDropdownToggle.enabled = true;
        dispatch(setSwapDropdownToggleEnabled(content?.assignedId));
        setIconOb(newIconOb);
        if (!isExpanded) {
          setIsExpanded(true);
        }
        setLocalDropdownVisible(false);
      },
      inActive: isRecurring || isNonDuplicableType,
    },
    {
      key: '3b',
      label: 'Revert to Parent Item',
      icon: '', // Empty icon to hide the default icomoon icon
      extra: (
        <ParentIconWrapper>
          <ParentIconBorderBox>
            <Text fontSize={10} style={{ marginTop: 2 }} color={colors.white}>
              P
            </Text>
          </ParentIconBorderBox>
        </ParentIconWrapper>
      ),
      action: () => {
        const formattedDate = moment(date).format('YYYY-MM-DD');
        const assignedId = content?.assignedId;
        cancelDebounceForTable(assignedId);
        dispatch(revertSwappedItemToParent(accountCode, assignedId, index, formattedDate));
        setLocalDropdownVisible(false);
      },
      inActive: !content?.swap,
    },
    {
      key: '4',
      label: !isRecurring ? 'Set as Recurring' : 'Update Recurring',
      icon: 'recurring-circle',
      action: () => {
        dispatch(setActiveModal('setRecurringItemModal', {
          ...content, itemIndex: index, isRecurring, effectiveDate,
        }));
        setLocalDropdownVisible(false);
      },
      inActive: programType === 'free-form-program' || content?.preSave || content?.exercise?.isDeleted || content?.tag?.isDeleted,
    },
    {
      key: '4-b',
      label: 'Update Future Occurrences',
      icon: 'double-checkmark',
      action: () => {
        const modalData = {
          content,
          index,
          date: moment(effectiveDate || selectedDate).format('YYYY-MM-DD'),
          assignedId: content.assignedId,
        };
        if (content.isInherited && content.calendarId) {
          modalData.isInherited = true;
          modalData.parentId = content.calendarId;
        }
        dispatch(setActiveModal('updateFutureOccurrencesModal', modalData));
        setLocalDropdownVisible(false);
      },
      inActive: !flags.ufo
        || isRecurring
        || content.preSave
        || content.type === 'video'
        || content.type === 'rest'
        || content.type === 'session_break'
        || programType === 'free-form-program',
    },
    { divider: true, inActive: false },
    {
      key: '4-a',
      label: getInfoMenuLabel(),
      icon: 'note2',
      action: () => {
        flushDebounceForTable(content.assignedId);
        dispatch(setActiveModal('addInfoOrDescriptionModal', {
          ...content,
          itemIndex: index,
          effectiveDate,
          dayNumber,
          prevLocAddInfo: localAdditionalInfo,
        }));
        setLocalDropdownVisible(false);
      },
      inActive: !shouldAddInfoShow(content),
    },
    {
      divider: true,
      inActive: isRecurring
      || !shouldAddInfoShow(content) || (calendarItemsForSelectedDate?.length === 1),
    },
    {
      key: '5',
      label: 'Move Up',
      icon: 'alt-up-arrow',
      action: () => {
        if (!isRecurring && index !== 0 && calendarItemsForSelectedDate?.length !== 1) {
          const originalItems = [...calendarItemsForSelectedDate];
          const newItems = Array.from(calendarItemsForSelectedDate);
          const [removed] = newItems.splice(index, 1);
          newItems.splice(index - 1, 0, removed);
          const newOrderedAssignmentIds = [];
          newItems.forEach((calendarItem) => {
            if (typeof calendarItem.assignedId !== 'string') {
              newOrderedAssignmentIds.push(calendarItem.assignedId);
            }
          });

          if (programType === 'free-form-program') {
            const submissionObject = {
              dayNumber,
              order: newOrderedAssignmentIds,
            };

            /** we need to set items here before checking length
             *  in case all items are pre save items
             * which would track newOrderAssignmentIds as 0 lenght
             */
            dispatch(setNonDateBasedProgramItems(newItems, dayNumber));
            if (newOrderedAssignmentIds?.length) {
              dispatch(
                updateWorkoutItemsOrder(currentUser.accountCode, submissionObject, originalItems),
              );
            }
          } else {
            const submissionObject = {
              date: moment(effectiveDate).format('YYYY-MM-DD'),
              order: newOrderedAssignmentIds,
              assignedIdsBeingMoved: [removed.assignedId],
              movedToIndex: newOrderedAssignmentIds.indexOf(removed.assignedId),
            };
            /** we need to set items here before checking length
             * in case all items are pre save items
             * which would track newOrderAssignmentIds as 0 lenght
             */
            dispatch(setCalendarItems(newItems, moment(effectiveDate).format('YYYY-MM-DD')));
            if (newOrderedAssignmentIds?.length) {
              dispatch(updateCalendarItemsOrder(
                currentUser.accountCode,
                submissionObject,
                originalItems,
              ));
            }
          }
        }
      },
      inActive: isRecurring || index === 0 || calendarItemsForSelectedDate?.length === 1,
    },
    {
      key: '6',
      label: 'Move Down',
      icon: 'alt-down-arrow',
      action: () => {
        if (!isRecurring
          && calendarItemsForSelectedDate?.length > 1
          && index !== (calendarItemsForSelectedDate?.length || 0) - 1) {
          const originalItems = [...calendarItemsForSelectedDate];
          const newItems = Array.from(calendarItemsForSelectedDate);
          const [removed] = newItems.splice(index, 1);
          newItems.splice(index + 1, 0, removed);

          const newOrderedAssignmentIds = [];
          newItems.forEach((calendarItem) => {
            if (typeof calendarItem.assignedId !== 'string') {
              newOrderedAssignmentIds.push(calendarItem.assignedId);
            }
          });

          if (programType === 'free-form-program') {
            const submissionObject = {
              dayNumber,
              order: newOrderedAssignmentIds,
            };
            /** we need to set items here as well incase all items are pre save items
             * which would track newOrderAssignmentIds as 0 lenght
             */
            dispatch(setNonDateBasedProgramItems(newItems, dayNumber));
            if (newOrderedAssignmentIds?.length) {
              dispatch(updateWorkoutItemsOrder(
                currentUser.accountCode,
                submissionObject,
                originalItems,
              ));
              dispatch(setNonDateBasedProgramItems(newItems, dayNumber));
            }
          } else {
            const submissionObject = {
              date: moment(effectiveDate).format('YYYY-MM-DD'),
              order: newOrderedAssignmentIds,
              assignedIdsBeingMoved: [removed.assignedId],
              movedToIndex: newOrderedAssignmentIds.indexOf(removed.assignedId),
            };
            /** we need to set items here as well incase all items are pre save items
             * which would track newOrderAssignmentIds as 0 lenght
             */
            dispatch(setCalendarItems(newItems, moment(effectiveDate).format('YYYY-MM-DD')));
            if (newOrderedAssignmentIds?.length) {
              dispatch(updateCalendarItemsOrder(
                currentUser.accountCode,
                submissionObject,
                originalItems,
              ));
              dispatch(setCalendarItems(newItems, moment(effectiveDate).format('YYYY-MM-DD')));
            }
          }
        }
      },
      inActive: isRecurring
      || index === (calendarItemsForSelectedDate.length ?? 0) - 1
      || calendarItemsForSelectedDate?.length === 1,
    },
    {
      divider: true,
      inActive: isRecurring
      || index === (calendarItemsForSelectedDate.length ?? 0) - 1
      || calendarItemsForSelectedDate?.length === 1,
    },
    {
      key: '14',
      label: content.groupingLetter ? 'Superset Label' : 'Superset',
      icon: 'superset',
      action: () => {},
      shortcut: 'superset',
      isChecked: false,
      inActive: contentType === 'session_break' || content?.type === 'questionnaire',
    },
    {
      key: '99',
      label: 'Remove Superset',
      icon: 'unlink',
      action: () => {
        /** create an new array of item that we can manipulate and set with
        * grouping letters and colors
        */
        let items;
        if (isRecurring === 'before') {
          items = [...beforeRecurringWorkouts];
        } else if (isRecurring === 'after') {
          items = [...afterRecurringWorkouts];
        } else {
          items = [...calendarItemsForSelectedDate];
        }

        /** we need to track what the original items values were */
        let originalCalendarItems;
        if (isRecurring === 'before') {
          originalCalendarItems = beforeRecurringWorkouts.map((item) => ({ ...item }));
        } else if (isRecurring === 'after') {
          originalCalendarItems = afterRecurringWorkouts.map((item) => ({ ...item }));
        } else {
          originalCalendarItems = calendarItemsForSelectedDate.map((item) => ({ ...item }));
        }

        /** original target */
        const originalTarget = originalCalendarItems.find(
          (item) => item.assignedId.toString() === content?.assignedId.toString(),
        );

        /** target to manipulate and mutate for setting in redux  */
        const targetItem = items.find(
          (item) => item.assignedId.toString() === originalTarget?.assignedId.toString(),

        );

        /** update/mutate these items in the array that we will set in redux */
        targetItem.groupingLetter = null;
        targetItem.groupingColorCode = null;

        if (isRecurring === 'before' || isRecurring === 'after') {
          const itemsTosend = isRecurring === 'before' ? beforeRecurringWorkouts : afterRecurringWorkouts;
          dispatch(setRecurringCalendarItems(itemsTosend, moment(effectiveDate).format('YYYY-MM-DD'), isRecurring));
        } else if (programType === 'free-form-program') {
          dispatch(setNonDateBasedProgramItems(items, dayNumber));
        } else {
          dispatch(setCalendarItems(items, moment(effectiveDate).format('YYYY-MM-DD')));
        }

        dispatch(setCombineWithInfo({
          /** if either the target item of the sourceItem grouping letter is the same
               * as the original then we don't need to set something to trigger an update
               */
          target: targetItem,
          source: null,
          isMultiSelect: false,
        }));
      },
      isChecked: false,
      inActive: !content.groupingLetter,
    },
    { divider: true, inActive: contentType === 'session_break' || content?.type === 'questionnaire' || content.type === 'health' },
    buildOptionMenuItem(optionsById[EXERCISE_OPTION_IDS.EACH_SIDE], '13'),
    { divider: true, inActive: !optionsById[EXERCISE_OPTION_IDS.EACH_SIDE]?.isAvailable },
    buildOptionMenuItem(optionsById[EXERCISE_OPTION_IDS.TRACK_VOLUME_LOAD], '7'),
    buildOptionMenuItem(optionsById[EXERCISE_OPTION_IDS.TRACK_REP_COUNT], '8'),
    { divider: true, inActive: !optionsById[EXERCISE_OPTION_IDS.TRACK_REP_COUNT]?.isAvailable },
    buildOptionMenuItem(optionsById[EXERCISE_OPTION_IDS.FORCE_PR_UPDATE], '9'),
    buildOptionMenuItem(optionsById[EXERCISE_OPTION_IDS.DISABLE_PR_UPDATE], '10'),
    { divider: true, inActive: !optionsById[EXERCISE_OPTION_IDS.DISABLE_PR_UPDATE]?.isAvailable },
    buildOptionMenuItem(optionsById[EXERCISE_OPTION_IDS.COMPLETION_ONLY], '11'),
    /** Directly under Completion Only: the two block each other, so keeping
     * them adjacent makes the greyed-out row and its tooltip easy to connect
     * to the row that caused it. */
    buildOptionMenuItem(optionsById[EXERCISE_OPTION_IDS.BODYWEIGHT], '12a'),
    buildOptionMenuItem(optionsById[EXERCISE_OPTION_IDS.COACH_COMPLETION], '12'),
    {
      key: '13a',
      label: 'Assign Pace',
      icon: 'clock',
      action: () => {},
      shortcut: 'pace',
      isChecked: false,
      inActive: contentType !== 'R',
    },
    {
      key: '13b',
      label: 'View in Journal',
      icon: 'journal',
      action: handleViewInJournal,
      inActive: !hasSelectedAthleteInBuild || !journalExercise?.id,
    },
    { divider: true, inActive: contentType === 'S' || contentType === 'CD' || content?.type === 'warm_up' || contentType === 'N' || content?.type === 'video' || content?.type === 'rest' || contentType === 'session_break' || content?.type === 'progression' || content.type === 'P' },
    {
      key: '15',
      label: 'Delete',
      icon: 'delete',
      action: () => {
        if (content.preSave) {
          handleRemovePresaveItemFromRedux();
        } else {
          // Show confirmation modal instead of direct deletion
          const isParentCalendarItem = content.isInherited;
          dispatch(setActiveModal('confirmDeleteModal', {
            assignedId: content.assignedId,
            itemIndex: index,
            isRecurring,
            isParentCalendarItem,
            ...(programType === 'free-form-program' && { dayNumber }),
            /** Always pass the date when available — the month-view
             * popover provides effectiveDate but isWeekView is false
             * there, so the old `isWeekView && ...` guard missed it. */
            ...(effectiveDate && { date: effectiveDate }),
          }));
        }
        setLocalDropdownVisible(false);
      },
      color: '#FF0505',
      shortcut: 'Del or Ctrl+D',
      inActive: false,
    },
  ];

  /** The rows arrow keys can actually land on: dividers, rows hidden for
   * this item type, and inert rows are all skipped. */
  const navItems = menuItems.filter((item) => (
    !item.inActive
    && !item.divider
    && !item.disabled
  ));

  /** Read inside the keydown handler through refs so the listener re-attaches
   * only when the menu opens/closes or a submenu is entered/exited. Both are
   * re-derived every render — including renders driven by unrelated Redux slices
   * such as allExercises and calendarContainerScrollPosition — so depending on
   * them directly churned a window listener on every one. Refreshed on each
   * render, so the handler never reads a stale value.
   *
   * Refs rather than useMemo because navItems can't be memoized here: menuItems
   * is a fresh array literal every render, and renderMenuItems below matches
   * rows by identity (navItems.indexOf(item)) — a memoized navItems would return
   * -1 for every row and silently disable the whole keyboard nav. */
  const navItemsRef = useRef(navItems);
  navItemsRef.current = navItems;
  const focusedIndexRef = useRef(focusedIndex);
  focusedIndexRef.current = focusedIndex;

  /** Clicking the row is the only thing that opens (or closes) one of the
   * nested pickers: rc-menu never registers an eventKey for those SubMenus —
   * the VisibleClickDiv between <Menu> and <SubMenu> breaks it — so driving
   * `openKeys` from outside does nothing and rc-trigger never renders the
   * popup. Clicking runs the exact sequence the mouse does. */
  const toggleSubmenuRow = (navIndex) => {
    menuRef.current?.querySelector(`[data-nav-index="${navIndex}"]`)?.click();
  };

  const exitSubmenu = () => {
    if (openSubmenu) toggleSubmenuRow(openSubmenu.navIndex);
    setOpenSubmenu(null);
  };

  /** Escape has to be claimed in the window CAPTURE phase, because every other
   * Escape handler in play is a bubble-phase window listener registered BEFORE
   * ours: rc-dropdown's useAccessibility closes the whole menu on Escape
   * (node_modules/rc-dropdown/lib/hooks/useAccessibility.js), and in month view
   * MonthEditPopover collapses the entire exercise row. Same node, earlier
   * registration — so stopPropagation, and even stopImmediatePropagation, from
   * inside a picker is always too late. Capture at window is the only point that
   * runs first, so it's the only place one-layer-per-press can be enforced.
   *
   * Guarding rc-dropdown's onVisibleChange instead would not work: it captures
   * its handler once, keyed on [visible], so its closure holds a stale
   * openSubmenu of null.
   *
   * Claiming it this aggressively means we also have to hand it back: while a
   * modal opened from this menu is up, that modal owns Escape. react-modal
   * listens in the bubble phase (ModalPortal's onKeyDown), so without the
   * stand-down below a capture-phase kill here reaches it first and the coach's
   * Escape closes nothing visible. Same guard MonthEditPopover uses. */
  useEffect(() => {
    if (!localDropdownVisible) return undefined;

    const handleEscapeCapture = (e) => {
      if (e.key !== 'Escape') return;
      if (isAnyModalOpen) return;
      e.preventDefault();
      /** Peel one layer and stop the event dead, so neither rc-dropdown nor
       * MonthEditPopover sees it. */
      e.stopImmediatePropagation();
      if (openSubmenu) {
        exitSubmenu();
      } else {
        setLocalDropdownVisible(false);
      }
    };

    window.addEventListener('keydown', handleEscapeCapture, true);
    return () => window.removeEventListener('keydown', handleEscapeCapture, true);
  }, [localDropdownVisible, openSubmenu, isAnyModalOpen]);

  useEffect(() => {
    if (!localDropdownVisible) return undefined;

    const handleKeyDown = (e) => {
      /** While a nested picker has the keyboard it owns every key. Bailing
       * here is what stops Escape from closing the whole menu out from
       * under a submenu the coach is still using. */
      if (openSubmenu) return;

      const focused = navItemsRef.current[focusedIndexRef.current];

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((prev) => Math.min(prev + 1, navItemsRef.current.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((prev) => Math.max(prev - 1, 0));
      } else if ((e.key === 'ArrowRight' || e.key === 'Enter') && isSubmenuItem(focused)) {
        e.preventDefault();
        toggleSubmenuRow(focusedIndexRef.current);
        setOpenSubmenu({ key: focused.key, navIndex: focusedIndexRef.current });
      } else if (e.key === 'Enter' && focused) {
        e.preventDefault();
        /** Run the action and nothing else, exactly like clicking the row.
         * Closing is the action's own job — the ones that open a modal call
         * setLocalDropdownVisible(false) themselves. Closing here instead
         * would yank the menu away from rows meant to stay open (the
         * property toggles, Move Up/Down, Expand/Collapse) and put the
         * keyboard out of parity with the mouse. */
        focused.action();
      }
      /** Escape is handled by the capture-phase listener above, which stops the
       * event before it ever reaches this bubble-phase handler. */
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    /** navItems and focusedIndex are deliberately omitted — read through refs
     * above so this listener isn't re-attached on every render. openSubmenu
     * stays, because the bail-out guard reads it from this closure. */
  }, [localDropdownVisible, openSubmenu]);

  /** Start on the first row each time the menu opens, and drop any
   * drilled-into submenu when it closes. */
  useEffect(() => {
    setFocusedIndex(localDropdownVisible ? 0 : -1);
    if (!localDropdownVisible) setOpenSubmenu(null);
  }, [localDropdownVisible]);

  /** DOM focus never moves, so nothing scrolls the highlighted row into
   * view on its own — and the list is taller than the menu. Matched by
   * attribute rather than ref because these are rc-menu Items, which don't
   * forward refs reliably. */
  useEffect(() => {
    if (focusedIndex < 0) return;
    menuRef.current
      ?.querySelector(`[data-nav-index="${focusedIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex]);

  const renderMenuItems = () => menuItems.map((item, renderIndex) => {
    if (item.inActive) return null;

    if (item.divider) return <CustomDivider key={`divider-${renderIndex}`} />;

    const navIndex = navItems.indexOf(item);

    return (
      <HoverableMenuItem
        key={item.key}
        hoverableItem={item}
        content={content}
        isRecurring={isRecurring}
        assignedPace={assignedPace}
        setAssignedPace={setAssignedPace}
        effectiveDate={effectiveDate}
        dayNumber={dayNumber}
        measure={measure}
        navIndex={navIndex}
        isFocused={navIndex !== -1 && navIndex === focusedIndex}
        isKeyboardOpen={openSubmenu?.key === item.key}
        onExitSubmenu={exitSubmenu}
      />
    );
  });

  return (
    <Dropdown
      trigger={['click']}
      overlay={(
        <MenuWrapper ref={menuRef} onClick={(e) => e.stopPropagation()}>
          {/* The superset/pace submenu pops out via rc-menu into a body-level
            * container the emotion theme can't reach — override its default
            * white chrome with captured colors. */}
          <Global
            styles={css`
              .custom-submenu-supersetPicker ul.rc-menu,
              .pace-dropdown ul.rc-menu {
                background: ${colors.surfaceElevated} !important;
                border: 1px solid ${colors.borderLight} !important;
                box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.25) !important;
              }
              .custom-submenu-supersetPicker .rc-menu-item-active,
              .custom-submenu-supersetPicker .rc-menu-item-selected,
              .pace-dropdown .rc-menu-item-active,
              .pace-dropdown .rc-menu-item-selected {
                background-color: transparent !important;
              }
            `}
          />
          <Menu
            triggerSubMenuAction='click'
            className='custom-rc-menu'
            style={{
              width: 280,
              minHeight: 100,
              /** minus 20 for a bit of padding */
              maxHeight: maxDropdownHeight - 20,
              overflowY: 'auto',
              marginBottom: '0px',
              border: `1px solid ${colors.borderLight}`,
              background: colors.surfaceElevated,
            }}
          >
            <MenuHeader>
              <Text fontSize='16px' fontWeight={700} color={colors.textSecondary}>Actions</Text>
              <Text fontSize='16px' color={colors.textSecondary}>{OPTIONS_SHORTCUT_LABEL}</Text>
            </MenuHeader>
            {renderMenuItems()}
          </Menu>
        </MenuWrapper>
      )}
      visible={localDropdownVisible}
      onVisibleChange={(visible) => {
        if (visible) {
          openDropdown();
        } else {
          setLocalDropdownVisible(false);
        }
      }}
    >
      <DropdownContainer ref={dropdownRef} data-tooltip-id={`${content.assignedId}`}>
        <DropdownIcon>
          <IcomoonReact
            iconSet={iconSet}
            size={18}
            icon='dots'
            className='AdditionalOptionsDots'
            style={{ transform: 'rotate(90deg)' }}
          />
        </DropdownIcon>
        <ReactTooltip
          id={`${content.assignedId}`}
          place='top'
          content={`Additional Options (${OPTIONS_SHORTCUT_LABEL})`}
          style={{
            backgroundColor: 'rgba(68, 68, 68, 1)',
            color: colors.white,
            fontFamily: 'Nunito Sans',
            fontSize: '13px',
            zIndex: 1000,
          }}
        />
      </DropdownContainer>
    </Dropdown>
  );
};

CalendarItemDropdownOptions.defaultProps = {
  localDescription: '',
  isRecurring: '',
  assignedPace: '',
  setAssignedPace: () => {},
  isWeekView: false,
  effectiveDate: null,
  dayNumber: null,
  isDragging: false,
  flags: {},
  exerciseOptions: [],
};

CalendarItemDropdownOptions.propTypes = {
  isDragging: PropTypes.bool,
  isExpanded: PropTypes.bool.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  index: PropTypes.number.isRequired,
  expandId: PropTypes.number.isRequired,
  setIsExpanded: PropTypes.func.isRequired,
  iconOb: PropTypes.instanceOf(Object).isRequired,
  setIconOb: PropTypes.func.isRequired,
  handleRemovePresaveItemFromRedux: PropTypes.func.isRequired,
  contentType: PropTypes.string.isRequired,
  localAdditionalInfo: PropTypes.string.isRequired,
  localDescription: PropTypes.string,
  isRecurring: PropTypes.string,
  assignedPace: PropTypes.string,
  setAssignedPace: PropTypes.func,
  isWeekView: PropTypes.bool,
  effectiveDate: PropTypes.string,
  dayNumber: PropTypes.number,
  measure: PropTypes.func.isRequired,
  flags: PropTypes.shape({
    ufo: PropTypes.bool,
  }),
  exerciseOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    menuLabel: PropTypes.string,
    isChecked: PropTypes.bool,
    isAvailable: PropTypes.bool,
    toggle: PropTypes.func,
  })),
};

export default withLDConsumer()(CalendarItemDropdownOptions);
